package io.horion.auth.presentation.ui.components.profile

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import org.jetbrains.compose.web.css.*

@Composable
fun ProfileHomeSection(viewModel: AuthViewModel) {
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val strings = AppLanguage.strings
    val state = viewModel.uiState

    // 1. Détermination de l'identifiant principal (Email ou Téléphone)
    val userEmail = state.userEmail?.takeIf { it.isNotBlank() }
    val userPhone = state.userPhone?.takeIf { it.isNotBlank() }

    // Libellé secondaire affiché sous le prénom (priorité à l'email, sinon téléphone)
    val displayContact = userEmail ?: userPhone ?: strings.emptyField

    // 2. Calcul du nom / prénom à afficher
    val firstName = state.userFirstName?.takeIf { it.isNotBlank() }
    val lastName = state.userLastName?.takeIf { it.isNotBlank() }

    val userName = when {
        !firstName.isNullOrEmpty() || !lastName.isNullOrEmpty() -> {
            listOfNotNull(firstName, lastName).joinToString(" ").replaceFirstChar { it.uppercase() }
        }
        userEmail != null -> {
            userEmail.substringBefore("@").replaceFirstChar { it.uppercase() }
        }
        userPhone != null -> userPhone
        else -> strings.emptyField
    }

    Column(modifier = Modifier.fillMaxWidth().gap(24.px)) {

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(24.px)
                .borderRadius(16.px)
                .backgroundColor(colors.surface)
                .border(1.px, LineStyle.Solid, colors.border)
                .styleModifier { property("box-shadow", "0 1px 4px rgba(0,0,0,0.04)") },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            // avatar + nom + contact (email/téléphone) + badges
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.gap(20.px)
            ) {
                // Avatar + icône caméra
                Box(
                    modifier = Modifier
                        .size(80.px)
                        .borderRadius(50.percent)
                        .backgroundColor(colors.inputBg)
                        .border(2.px, LineStyle.Solid, colors.border)
                        .styleModifier { property("position", "relative") },
                    contentAlignment = Alignment.Center
                ) {
                    FaUser(modifier = Modifier.fontSize(30.px).color(colors.mutedText))
                    Box(
                        modifier = Modifier
                            .size(24.px)
                            .borderRadius(50.percent)
                            .backgroundColor(colors.border)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("position", "absolute")
                                property("bottom", "0")
                                property("right", "0")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        FaCamera(modifier = Modifier.fontSize(10.px).color(colors.mutedText))
                    }
                }

                // Nom + Email / Téléphone + badges
                Column(modifier = Modifier.gap(6.px)) {
                    SpanText(
                        userName,
                        modifier = Modifier
                            .fontSize(AuthTypography.h3)
                            .fontWeight(FontWeight.Bold)
                            .color(colors.onBackground)
                    )

                    SpanText(
                        displayContact,
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .color(colors.mutedText)
                    )

                    Box(modifier = Modifier.height(4.px))

                    // Badges Rôle + séparateur + Statut
                    Row(
                        modifier = Modifier.gap(16.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Rôle
                        Column(modifier = Modifier.gap(4.px)) {
                            SpanText(
                                strings.roleLabel,
                                modifier = Modifier
                                    .fontSize(AuthTypography.tiny)
                                    .color(colors.mutedText)
                            )
                            HomeBadge(
                                label = state.userRole.lowercase(),
                                bgColor = colors.badgeRoleBg,
                                textColor = colors.badgeRoleText
                            )
                        }

                        // Séparateur vertical
                        Box(
                            modifier = Modifier
                                .width(1.px)
                                .height(36.px)
                                .backgroundColor(colors.border)
                        )

                        // Statut
                        Column(modifier = Modifier.gap(4.px)) {
                            SpanText(
                                strings.accountStatusLabel,
                                modifier = Modifier
                                    .fontSize(AuthTypography.tiny)
                                    .color(colors.mutedText)
                            )
                            HomeBadge(
                                label = strings.accountActive,
                                bgColor = colors.badgeActiveBg,
                                textColor = colors.badgeActiveText
                            )
                        }
                    }
                }
            }

            //  dernière connexion + localisation
            Column(
                modifier = Modifier.gap(8.px),
                horizontalAlignment = Alignment.End
            ) {
                SpanText(
                    strings.lastLogin,
                    modifier = Modifier
                        .fontSize(AuthTypography.tiny)
                        .color(colors.mutedText)
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.gap(6.px)
                ) {
                    FaCalendar(modifier = Modifier.fontSize(12.px).color(colors.mutedText))
                    SpanText(
                        state.loginTimestamp,
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .color(colors.onBackground)
                    )
                }
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.gap(6.px)
                ) {
                    FaLocationDot(modifier = Modifier.fontSize(12.px).color(colors.mutedText))
                    SpanText(
                        "${state.city}, ${state.country}",
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .color(colors.onBackground)
                    )
                }
            }
        }

        //  Infos perso + Sécurité
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .gap(20.px)
                .styleModifier { property("flex-wrap", "wrap") }
        ) {
            Box(
                modifier = Modifier
                    .weight(1)
                    .styleModifier { property("min-width", "280px") }
            ) {
                ProfilePersonalInfoSection(viewModel = viewModel)
            }

            Box(
                modifier = Modifier
                    .weight(1)
                    .styleModifier { property("min-width", "280px") }
            ) {
                ProfileSecuritySection(
                    viewModel = viewModel
                )
            }
        }
    }
}

@Composable
private fun HomeBadge(
    label: String,
    bgColor: Color,
    textColor: Color,
) {
    Box(
        modifier = Modifier
            .padding(leftRight = 10.px, topBottom = 4.px)
            .borderRadius(20.px)
            .backgroundColor(bgColor),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .fontSize(AuthTypography.tiny)
                .fontWeight(FontWeight.SemiBold)
                .color(textColor)
        )
    }
}